import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@common/components/button';
import { Container } from '@common/components/container';
import DataTable from '@common/components/datatable';
import { SortConfig } from '@common/components/datatable/types';
import Overview from '@common/components/overview';
import Placeholder from '@common/components/placeholder';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import { useRequest } from '@common/services/api';
import { downloadCSV } from '@common/utils/file/download-file';
import DateRangeSelectorComponent, {
  getDates,
} from '@modules/analytics/components/date-range-selector';
import { selected } from '@modules/organisation/selectors/organisation';

import useColumns from './useColumns';
import { ApiGeneralStatistics } from './types';
import { Bar } from '@common/components/bar';

const GeneralStatisticsContainer = () => {
  const { t } = useTranslation();
  const [selectedDateValue, setSelectedDateValue] = useState<number>(2);
  const [dates, setDates] = useState(getDates(2));
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'invited_users',
    sortOrder: 'asc',
  });
  const organisation = useAppSelector(selected);
  const didMount = useDidComponentMount();

  const payload = useMemo(
    () => ({
      from_date: dates[0].start.format('YYYY-MM-DD'),
      to_date: dates[0].end.format('YYYY-MM-DD'),
    }),
    [dates]
  );

  const apiConfig = {
    payload,
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((network: ApiGeneralStatistics) => ({
          ...network,
        })),
      };
    },
  };

  const {
    data: generalStatisticsData,
    setData: setGeneralStatisticsData,
    loading,
    reset,
  } = useRequest(
    `/v1/organisations/${organisation.id}/analytics/networks`,
    apiConfig
  );

  useEffect(() => {
    if (!didMount) return;
    reset();
  }, [didMount, reset]);

  const columns = useColumns(generalStatisticsData);

  const handleSort = useCallback(
    (key: string) => {
      let updatedSortConfig: SortConfig = sortConfig;
      if (key !== sortConfig.key) {
        updatedSortConfig = { key, sortOrder: 'asc' };
      } else if (sortConfig.sortOrder === 'asc') {
        updatedSortConfig = { key, sortOrder: 'desc' };
      } else {
        updatedSortConfig = { key, sortOrder: 'asc' };
      }
      const sortedData = [...generalStatisticsData].sort((a, b) => {
        const aValue = a[updatedSortConfig.key];
        const bValue = b[updatedSortConfig.key];

        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return updatedSortConfig.sortOrder === 'asc'
            ? comparison
            : -comparison;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const comparison = aValue - bValue;
          return updatedSortConfig.sortOrder === 'asc'
            ? comparison
            : -comparison;
        }

        // Fallback for mixed types or other cases
        const aStr = String(aValue);
        const bStr = String(bValue);
        const comparison = aStr.localeCompare(bStr);
        return updatedSortConfig.sortOrder === 'asc' ? comparison : -comparison;
      });
      setSortConfig(updatedSortConfig);
      setGeneralStatisticsData(sortedData);
    },
    [generalStatisticsData, sortConfig]
  );

  const handleSetDateRange = ({
    value,
    dates: newDates,
  }: {
    value: number;
    dates: any[];
  }) => {
    setSelectedDateValue(value);
    setDates(newDates);
  };

  const downloadCSVData = () => {
    if (!generalStatisticsData || generalStatisticsData.length === 0) {
      return;
    }

    const fromDate = dates[0].start.format('YYYY-MM-DD');
    const toDate = dates[0].end.format('YYYY-MM-DD');
    const dateRangeLabel = `${fromDate}_to_${toDate}`;

    const headers = [
      t('organisation:general_statistics_community_name'),
      t('organisation:general_statistics_invited_users'),
      t('organisation:general_statistics_registered_users'),
      t('organisation:general_statistics_registered_users_percentage'),
      t('organisation:general_statistics_active_users'),
      t('organisation:general_statistics_active_users_percentage'),
      t('organisation:general_statistics_posts_created'),
      t('organisation:general_statistics_comments_created'),
      t('organisation:general_statistics_likes_given'),
      t('organisation:general_statistics_chat_messages_sent'),
    ];

    const filteredData = generalStatisticsData.map(
      (item: ApiGeneralStatistics) => {
        const { network_id, ...filteredItem } = item;
        return filteredItem;
      }
    );

    downloadCSV({
      headers,
      data: filteredData,
      fileName: `general-statistics-${dateRangeLabel}.csv`,
    });
  };

  return (
    <Container name="General Statistics" className="tw-max-w-[calc(100%-265px)]">
      <Container.Content>
        <Bar className="!tw-pb-0">
          <h2>{t('organisation:general_statistics_title')}</h2>

          <div className="tw-flex tw-flex-row tw-justify-end tw-gap-4">
            <DateRangeSelectorComponent
              value={selectedDateValue}
              onChange={handleSetDateRange}
            />
            <Button
              onClick={downloadCSVData}
              size="large"
              icon="download"
              className="tw-mb-4"
            >
              {t('core:export')}
            </Button>
          </div>
        </Bar>
        <Overview>
          <DataTable
            columns={columns}
            data={generalStatisticsData || []}
            loading={loading}
            stickyHeader
            stickyFirstColumn
            sortConfig={sortConfig}
            onSort={handleSort}
            emptyView={
              <Placeholder
                title={t('organisation:general_statistics_no_data')}
              />
            }
          />
        </Overview>
      </Container.Content>
    </Container>
  );
};

export default GeneralStatisticsContainer;
